package org.example.project.setup

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object SetupNodeApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadNodeFirmenId(): String =
        runCatching {
            val settings = window.fetch("/node/status").then { it.json() }.await()
            (settings.asDynamic().firmenId as? String).orEmpty()
        }.getOrDefault("")

    suspend fun loadAllUsers(): List<All> =
        decodeList(LumaticaApiManager.call("user"))

    suspend fun loadUserById(id: String): All? =
        decodeOne(LumaticaApiManager.call("user/get/${apiPart(id)}"))

    suspend fun saveUser(user: All): All? {
        val existing = user.id.takeIf { it.isNotBlank() }?.let { loadUserById(it) }
        val saveId = existing?.id?.takeIf { it.isNotBlank() } ?: "neu"
        return decodeOne(
            LumaticaApiManager.call(
                listOf(
                    "user",
                    "save",
                    saveId,
                    user.personalnummer,
                    user.vorname,
                    user.nachname,
                    user.email,
                    user.rolle,
                    user.position,
                    user.bereich,
                    user.zugangDesktop.toString(),
                    user.zugangMobil.toString(),
                    user.terminkalenderOnline.toString(),
                    user.datenschutzAkzeptiert.toString(),
                    user.passwortVerifiziert.toString(),
                    user.terminkalenderIntern.toString(),
                    user.profilOnline.toString(),
                    user.profilIntern.toString(),
                    user.snapAnnehmen.toString(),
                    user.firma,
                    user.firmenId,
                    user.kunde,
                    user.name,
                    user.kuerzel,
                    ""
                ).joinToString("/") { apiPart(it) }
            )
        ) ?: error("User konnte nicht gespeichert werden.")
    }

    suspend fun changeUserPassword(userId: String, password: String): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/user/password-change-json",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("userId", userId)
                        put("password", password)
                    }
                )
            )
        ).await()
        return decodeOne(response.text().await())
    }

    suspend fun loadRegisterCompany(firmenId: String): All? =
        RegistrierungApi.loadCompanies().firstOrNull { it.firmenId == firmenId || it.id == firmenId }

    suspend fun loadCompanyDeckblatt(companyKey: String): All? =
        decodeOne(LumaticaApiManager.call("kartei/standort-deckblatt/${apiPart(currentUserId())}/${apiPart(companyKey)}"))

    suspend fun saveCompanyDeckblatt(companyKey: String, state: All): All? =
        decodeOne(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "standort-deckblatt-save",
                    companyKey,
                    state.id,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.webseite,
                    state.gegruendetAm
                ).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun loadStandorte(companyKey: String): List<All> =
        decodeList(LumaticaApiManager.call("kartei/standorte/${apiPart(currentUserId())}/${apiPart(companyKey)}"))

    suspend fun saveStandort(companyKey: String, state: All): All? =
        decodeOne(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "standort-save",
                    companyKey,
                    state.id,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.istnetzwerk.toString(),
                    state.istkunde.toString(),
                    state.firmenId,
                    state.standort,
                    state.strasse,
                    state.hausnummer,
                    state.plz,
                    state.ort,
                    state.land,
                    state.telefon,
                    state.email,
                    state.palettentauschAktiv.toString(),
                    state.palettentauschWeAktiv.toString(),
                    state.palettentauschWaAktiv.toString(),
                    state.halleVon,
                    state.halleBis,
                    state.regalVon,
                    state.regalBis,
                    state.fachVon,
                    state.fachBis,
                    state.ebeneVon,
                    state.ebeneBis,
                    state.toreVon,
                    state.toreBis,
                    state.parkplatzVon,
                    state.parkplatzBis,
                    json.encodeToString(state.standortLabels),
                    companyKey
                ).joinToString("/") { apiPart(it) }
            )
        )

    private fun decodeOne(raw: String): All? =
        if (raw.isBlank() || raw == "null") null
        else runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun currentUserId(): String = LoginService.getId().trim()

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
